import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, Badge, Field } from "@/components/ui-hud";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowRight, CheckCircle2, Loader2, ExternalLink, IdCard } from "lucide-react";
import { Link } from "react-router-dom";
import BackToCockpit from "@/components/BackToCockpit";

export default function Wallet() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [digitalId, setDigitalId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/payouts/status").then((r) => setStatus(r.data)).catch(() => setStatus({ has_account: false }));
    api.get(`/id/${user?.pjp_id?.public_token || "xxx"}`)
      .then((r) => setDigitalId(r.data))
      .catch(() => setDigitalId(null));
  }, [user?.id]);

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-16">Sign in to view your PJP Wallet.</div>;

  const onboard = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payouts/onboard-link", { origin_url: window.location.origin });
      if (!redirectExternal(r.data.onboarding_url)) toast.error("Popup blocked. Please allow pop-ups and try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
    } finally { setBusy(false); }
  };

  const dashboard = async () => {
    try {
      const r = await api.post("/payouts/dashboard-link");
      window.open(r.data.dashboard_url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open dashboard"); }
  };

  const enableId = async () => {
    try {
      const r = await api.post("/id/mine/enable");
      toast.success("Digital ID enabled");
      const r2 = await api.get(`/id/${r.data.public_token}`);
      setDigitalId(r2.data);
    } catch (e) {
      // 402 already pops the global UpgradeGateModal via axios interceptor.
      if (e?.response?.status !== 402) {
        toast.error(e?.response?.data?.detail || "Enable failed");
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <BackToCockpit />
      <SectionTitle overline="// PJP WALLET" title="Get paid for the trips you fly." subtitle="Onboard Stripe Connect once — bonuses, day-rate escrow, and future charter payouts all land in your PJP wallet, ACH'd to your bank." />

      <div className="grid md:grid-cols-2 gap-5">
        {/* Stripe Connect card */}
        <Card testId="wallet-connect">
          <div className="flex items-center gap-2 mb-3">
            <WalletIcon className="h-4 w-4 text-cyan" />
            <div className="overline text-cyan">// STRIPE CONNECT</div>
          </div>
          {!status && <Loader2 className="h-5 w-5 animate-spin text-cyan" />}
          {status && !status.has_account && (
            <>
              <h3 className="font-display text-xl font-bold tracking-tight mb-2">Not onboarded yet.</h3>
              <p className="text-sm text-white/60 mb-4">Onboard through Stripe Express — takes ~2 min. You'll add your bank and verify identity once.</p>
              <button onClick={onboard} disabled={busy} data-testid="btn-connect-onboard" className="btn btn-primary">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Onboard with Stripe <ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            </>
          )}
          {status && status.has_account && (
            <>
              <h3 className="font-display text-xl font-bold tracking-tight mb-2">
                {status.payouts_enabled ? "Payouts enabled ✓" : "Setup in progress"}
              </h3>
              <div className="grid grid-cols-3 gap-2 my-3">
                <MicroStat label="Payouts" v={status.payouts_enabled ? "Live" : "Pending"} ok={status.payouts_enabled} />
                <MicroStat label="Charges" v={status.charges_enabled ? "Yes" : "No"} ok={status.charges_enabled} />
                <MicroStat label="Details" v={status.details_submitted ? "Done" : "Todo"} ok={status.details_submitted} />
              </div>
              {status.requirements_due?.length > 0 && (
                <div className="text-[11px] text-orange-hud mono mb-3">
                  Stripe still needs: {status.requirements_due.slice(0, 3).join(", ")}{status.requirements_due.length > 3 ? "…" : ""}
                </div>
              )}
              <div className="flex gap-2">
                {!status.payouts_enabled && <button onClick={onboard} className="btn btn-primary">Finish onboarding <ArrowRight className="h-3.5 w-3.5" /></button>}
                <button onClick={dashboard} className="btn">Stripe Dashboard <ExternalLink className="h-3.5 w-3.5" /></button>
              </div>
            </>
          )}
        </Card>

        {/* Digital ID card */}
        <Card testId="wallet-digital-id">
          <div className="flex items-center gap-2 mb-3">
            <IdCard className="h-4 w-4 text-orange-hud" />
            <div className="overline text-orange-hud">// PJP DIGITAL ID</div>
          </div>
          {digitalId ? (
            <>
              <h3 className="font-display text-xl font-bold tracking-tight mb-2">{digitalId.name}</h3>
              <div className="mono text-xs text-white/60 mb-4">{digitalId.role?.toUpperCase()} · {digitalId.badge_label}</div>
              <Link to={`/id/${digitalId.public_token}`} data-testid="btn-view-digital-id" className="btn btn-primary">View my Digital ID <ArrowRight className="h-3.5 w-3.5" /></Link>
            </>
          ) : (
            <>
              <h3 className="font-display text-xl font-bold tracking-tight mb-2">Enable your Digital ID.</h3>
              <p className="text-sm text-white/60 mb-4">One-click, shareable NFC-ready badge that anyone can verify. Include ratings, cert level, hours, FAA-verified checkmark.</p>
              <button onClick={enableId} data-testid="btn-enable-digital-id" className="btn btn-primary">Enable Digital ID <ArrowRight className="h-3.5 w-3.5" /></button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function MicroStat({ label, v, ok }) {
  return (
    <div>
      <div className="eyebrow-muted mb-0.5">{label}</div>
      <div className={`mono text-sm ${ok ? "text-avail" : "text-orange-hud"}`}>{v}</div>
    </div>
  );
}
